import { Checkbox } from "@mantine/core";
import { useEffect, useState } from "react";

import type { FC, ReactElement, ReactNode } from "react";

type ChildrenProps = {
  checkStatus: CheckStatus;
  getRowCheckbox: (id: string) => ReactElement;
  isSelected: (id: string) => boolean;
  onToggleAll: () => void;
  reset: () => void;
  selected: string[];
};

export type CheckStatus = "indeterminate" | "unchecked" | "checked";

export type ListTableSelectionContextProps = {
  children: ({}: ChildrenProps) => ReactNode;
  data: string[];
  maxSelection?: number;
  resetOnChange?: boolean;
};

export const ListTableSelectionContext: FC<ListTableSelectionContextProps> = ({
  children,
  data,
  maxSelection = 0,
  resetOnChange = false,
}) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const checkStatus = getCheckStatus(selected, data);

  function getCheckStatus(selected: Set<string>, data: string[]): CheckStatus {
    if (selected.size === 0) return "unchecked";

    const allData = new Set(data);
    if (allData.isSubsetOf(selected)) {
      return "checked";
    }
    return "indeterminate";
  }

  function getRowCheckbox(id: string) {
    return <Checkbox checked={selected.has(id)} onClick={() => handleRowClick(id)} />;
  }

  function isSelected(id: string) {
    return selected.has(id);
  }

  function handleRowClick(id: string) {
    const updatedSelection = new Set(selected);
    if (updatedSelection.has(id)) {
      updatedSelection.delete(id);
    } else {
      if (maxSelection === 0 || updatedSelection.size < maxSelection) {
        updatedSelection.add(id);
      }
    }
    setSelected(updatedSelection);
  }

  function handleToggleAll() {
    if (checkStatus === "checked") {
      setSelected(new Set());
    } else {
      const updatedSelection = new Set(selected);
      setSelected(updatedSelection.union(new Set(data)));
    }
  }

  function reset() {
    setSelected(new Set());
  }

  useEffect(() => {
    if (resetOnChange) {
      setSelected(new Set());
    }
  }, [data]);

  return children({
    checkStatus,
    getRowCheckbox,
    isSelected,
    onToggleAll: handleToggleAll,
    reset,
    selected: Array.from(selected),
  });
};
